import React, { useState, useMemo } from 'react';
import { Clock, User, Bot, Shield, FileText, Filter, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { newSelect as Select } from '../ui/newSelect';

const eventTypeConfig = {
  ai_classification: { icon: Bot, color: 'bg-info-bg text-info', label: 'AI Classification' },
  ai_match: { icon: Bot, color: 'bg-info-bg text-info', label: 'AI Match' },
  factor_override: { icon: User, color: 'bg-warning-bg text-warning', label: 'Factor Override' },
  dedup_override: { icon: User, color: 'bg-warning-bg text-warning', label: 'Dedup Override' },
  classification_override: { icon: User, color: 'bg-warning-bg text-warning', label: 'Classification Override' },
  approval: { icon: Shield, color: 'bg-success-bg text-success', label: 'Approval' },
  rejection: { icon: Shield, color: 'bg-danger-bg text-danger', label: 'Rejection' },
  split_row: { icon: User, color: 'bg-info-bg text-info', label: 'Row Split' },
  note: { icon: FileText, color: 'bg-muted text-muted-foreground', label: 'Note' },
};

const filterOptions = [
  { value: 'all', label: 'All Events' },
  { value: 'ai_match', label: 'AI Matches' },
  { value: 'factor_override', label: 'Overrides' },
  { value: 'approval', label: 'Approvals' },
  { value: 'note', label: 'Notes' },
];

/**
 * Audit trail timeline component (Screen 16).
 * Shows all AI decisions, user overrides, and approvals for an import or org.
 * @param {object} props
 * @param {Array} props.events - audit trail events
 * @param {Function} [props.onExport] - CSV export handler
 */
const AuditTrail = ({ events = [], onExport }) => {
  const [typeFilter, setTypeFilter] = useState('all');

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return events;
    return events.filter((e) => e.eventType === typeFilter);
  }, [events, typeFilter]);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <Clock size={20} strokeWidth={1.5} />
          Audit Trail
        </h2>
        <div className='flex items-center gap-3'>
          <Select
            value={typeFilter}
            onValueChange={setTypeFilter}
            options={filterOptions}
            className='w-48'
          />
          {onExport && (
            <Button variant='outline' size='sm' onClick={onExport}>
              <Download size={14} strokeWidth={1.5} />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      <div className='relative'>
        <div className='absolute left-5 top-0 bottom-0 w-px bg-border' />
        <div className='flex flex-col gap-3'>
          {filtered.map((event, i) => {
            const cfg = eventTypeConfig[event.eventType] || eventTypeConfig.note;
            const Icon = cfg.icon;
            return (
              <div key={event.id || i} className='flex gap-3 relative'>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${cfg.color}`}>
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <Card className='flex-1 bg-card'>
                  <CardContent className='p-3'>
                    <div className='flex items-start justify-between'>
                      <div>
                        <Badge className={`${cfg.color} text-xs mb-1`}>{cfg.label}</Badge>
                        <p className='text-sm text-foreground'>{event.summary}</p>
                        {event.data?.reason && (
                          <p className='text-xs text-muted-foreground mt-1'>
                            Reason: {event.data.reason}
                          </p>
                        )}
                        {event.data?.notes && (
                          <p className='text-xs text-muted-foreground mt-0.5'>
                            Notes: {event.data.notes}
                          </p>
                        )}
                      </div>
                      <div className='text-right'>
                        <p className='text-xs text-muted-foreground'>
                          {event.actor === 'system' ? 'AI System' : event.actor}
                        </p>
                        <p className='text-xs text-muted-foreground'>
                          {event.timestamp ? new Date(event.timestamp).toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                    {event.data?.evidenceKey && (
                      <div className='mt-2 flex items-center gap-1 text-xs text-primary'>
                        <FileText size={12} strokeWidth={1.5} />
                        Evidence attached
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className='text-center text-muted-foreground py-8 pl-12'>No audit events found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;
